import { z } from "zod";
/**
 * Shared primitives for the BiteBalance MVP contract surface (T04).
 *
 * These types are the single source of truth: OpenAPI generation
 * (src/openapi.ts), the typed fetch client (src/client.ts), the mock
 * server (src/mock/), and the frontend all consume the same Zod schemas.
 *
 * Hard rule enforced throughout this package: no request schema anywhere
 * in this package may contain a `userId`/`user_id` field. Identity is
 * always derived server-side from validated JWT claims (see
 * tests/no-user-id.test.ts for the automated check).
 */
// ---------------------------------------------------------------------------
// Locale / ISO helpers
// ---------------------------------------------------------------------------
export const LocaleSchema = z.enum(["de", "en"]);
export const IsoDateTimeSchema = z.iso.datetime({ offset: true });
export const IsoDateSchema = z.iso.date();
// ---------------------------------------------------------------------------
// Idempotency / concurrency envelope
// ---------------------------------------------------------------------------
/** Header carried on every POST/PATCH/PUT/DELETE (see api-contracts.md contract rules). */
export const IdempotencyKeyHeaderSchema = z.object({
    "Idempotency-Key": z
        .string()
        .min(1)
        .max(255)
        .describe("Client-generated key (recommend UUIDv4). The server stores the terminal status and response per key so a duplicate mutation is a no-op that replays the original result."),
});
/** Included in request bodies of endpoints that mutate a revisioned aggregate. */
export const BaseRevisionSchema = z.object({
    baseRevision: z
        .number()
        .int()
        .nonnegative()
        .describe("Revision the client last observed; server rejects with 409 if stale."),
});
// ---------------------------------------------------------------------------
// Quantities: canonical (server truth) + display (locale-aware presentation)
// ---------------------------------------------------------------------------
export const CanonicalUnitSchema = z.enum(["g", "ml", "count"]);
export const CanonicalQuantitySchema = z.object({
    amount: z.number(),
    unit: CanonicalUnitSchema,
});
export const DisplayQuantitySchema = z.object({
    quantity: z.number(),
    unit: z.string().describe("Original/display unit, e.g. 'package', 'Bund', 'tbsp'."),
    unitKey: z.string().describe("i18n key for localized unit rendering."),
});
export const QuantitySchema = z.object({
    canonical: CanonicalQuantitySchema,
    display: DisplayQuantitySchema,
});
// ---------------------------------------------------------------------------
// Price / savings status — required on every monetary field (never invented)
// ---------------------------------------------------------------------------
/**
 * ADR-028 widened vocabulary. Rollout order matters (spec review I2): both
 * clients parse responses strictly, so web AND mobile must ship this widened
 * enum BEFORE the backend ever emits `cached`/`live`/`unavailable`. Both apps
 * consume this shared package, and web+backend deploy together from this repo
 * (mobile is Expo-Go dev), so same-repo atomicity covers it.
 * - `cached`: a real captured price served from our store (e.g. retailer offers).
 * - `live`: fetched from the source in-request (no producer yet).
 * - `unavailable`: honestly-missing price — replaces the fake €0.00 rows.
 */
export const PriceStatusSchema = z.enum([
    "estimated",
    "cached",
    "live",
    "stale",
    "confirmed",
    "unavailable",
]);
export const MoneySchema = z.object({
    amountCents: z.number().int(),
    currency: z.literal("EUR"),
    status: PriceStatusSchema,
    basis: z
        .string()
        .optional()
        .describe("Human-readable pricing basis, e.g. 'REWE avg, curated 2026-06'."),
});
// ---------------------------------------------------------------------------
// Deterministic validation contract (api-contracts.md, ADR-005/ADR-012)
// ---------------------------------------------------------------------------
/** The six stable, non-negotiable hard-failure reason codes. Never extend casually. */
export const ValidationReasonCodeSchema = z.enum([
    "ALLERGEN_CONFLICT",
    "HARD_EXCLUSION",
    "MISSING_COMPOSITION",
    "NUTRITION_OUT_OF_RANGE",
    "TIME_LIMIT_EXCEEDED",
    "BATCH_YIELD_MISMATCH",
]);
/**
 * ADR-055 (founder acceptance 2, 2026-09-04): STRUCTURED figures behind a
 * day-nutrition failure, emitted by the validator owner so clients and Amy
 * explain the consequence ("today would come to about 2,100 kcal — your range
 * goes up to 1,980") without parsing `sourceRef` or recomputing the range.
 * Integers in the metric's unit; `direction` says which bound was crossed.
 */
export const NutritionRangeDetailsSchema = z.object({
    dayIndex: z.number().int().nonnegative(),
    /** "day_kcal" | "day_protein" | … — the same subjectId as the failure. */
    subjectId: z.string(),
    unit: z.enum(["kcal", "g"]),
    target: z.number().int(),
    lowerBound: z.number().int(),
    upperBound: z.number().int(),
    actual: z.number().int(),
    direction: z.enum(["above", "below"]),
    /**
     * ADR-055 acceptance 4 — LIVE DAY: `live_day` when the day's total was
     * judged on actual intake for already-eaten food (outcome-scaled, plus
     * off-plan) + planned intake for remaining food; `planned_day` when every
     * meal counted at its planned calories (future days, generation).
     */
    basis: z.enum(["planned_day", "live_day"]).optional(),
});
export const ValidationFailureSchema = z.object({
    code: ValidationReasonCodeSchema,
    path: z.string(),
    subjectId: z.string(),
    sourceRef: z.string().nullable(),
    ruleVersion: z.string(),
    /** Present on NUTRITION_OUT_OF_RANGE day-band failures (ADR-055). */
    details: NutritionRangeDetailsSchema.optional(),
});
/** Soft issues — distinct from hard failures; can never disguise missing data as safe. */
export const ValidationWarningSchema = z.object({
    code: z.string(),
    path: z.string(),
    subjectId: z.string().optional(),
    messageKey: z.string().optional(),
    ruleVersion: z.string(),
});
export const ValidationResultSchema = z.object({
    status: z.enum(["PASS", "FAIL"]),
    ruleVersion: z.string(),
    failures: z.array(ValidationFailureSchema),
    warnings: z.array(ValidationWarningSchema),
    /** ADR-051 (stale-plan recovery): under MUTATION-SCOPED validation,
     * per-day nutrition-band findings on days the mutation does not touch
     * are reported here instead of failing the mutation — a stale future
     * week must not block a safe repair to today. These days are NOT
     * certified: only full regeneration validates them against current
     * settings. Absent under full-week validation (generation), which is
     * unchanged. */
    staleDays: z
        .array(z.object({ dayIndex: z.number().int(), reasonCodes: z.array(z.string()) }))
        .optional(),
});
// ---------------------------------------------------------------------------
// Plan proposal response (api-contracts.md "Plan proposal response")
// ---------------------------------------------------------------------------
export const PlanChangeTypeSchema = z.enum([
    "REPLACE_MEAL",
    "ADAPT_MEAL",
    "LOCK_MEAL",
    "UNLOCK_MEAL",
]);
export const PlanChangeMealSnapshotSchema = z.object({
    recipeId: z.string(),
    costCents: z.number().int(),
    kcal: z.number().int(),
    /**
     * A1/D-1 fix: the meal's serving count at this snapshot. Only populated
     * for a `scale_servings` change (before/after carry the old/new serving
     * count for the SAME recipeId) — omitted for REPLACE_MEAL/LOCK_MEAL/
     * UNLOCK_MEAL, where the serving count is unaffected by the change.
     * Optional/backward-compatible.
     */
    /** Fractional (1 / 1.5 / 2 — SUPPORTED_MEAL_SERVINGS): the generator's own portion multipliers. */
    servings: z.number().positive().optional(),
    /**
     * Swap-candidates change (2026-10-07): the recipe's hands-on minutes at
     * this snapshot, from the recipe row (never estimated). Optional and
     * additive — a proposal stored before this field existed, and any older
     * client, simply has none and renders nothing for it.
     */
    activeMinutes: z.number().int().nonnegative().optional(),
    /** Total minutes (incl. passive time) at this snapshot; omitted when the recipe row's total is not known to the proposal owner. */
    totalMinutes: z.number().int().nonnegative().optional(),
});
/**
 * ADR-028 rule 4 (offer snapshot at apply): the offer linkage captured when an
 * offer-backed `cheaper` proposal is created. Persisted verbatim into the plan
 * revision on apply so the later `offer` savings event and the provenance UI
 * never depend on the (expiring) offers table row. `retailer` uses the exact
 * OfferRetailerSchema vocabulary (offers.ts — kept as string here to avoid a
 * primitives→offers import cycle).
 */
export const PlanChangeOfferSchema = z.object({
    offerId: z.string(),
    retailer: z.string(),
    offerPriceCents: z.number().int().nonnegative(),
    validFrom: IsoDateSchema,
    validTo: IsoDateSchema,
    source: z.string().describe("Adapter/source id, e.g. 'marktguru'."),
});
/**
 * `reasonCodes` vocabulary produced today (services/planning createProposal;
 * badge copy in packages/i18n views.reasonBadges): USER_REQUESTED, LOWER_COST,
 * TIME_FIT, PANTRY_REUSE, BETTER_MATCH, COST_ESTIMATE_UNAVAILABLE, and — ADR-028
 * Phase 2 — OFFER_MATCH (the swapped-in recipe has at least one currently-valid
 * matched retailer offer; emitted alongside LOWER_COST when both apply). Kept as
 * open strings on the wire (clients render unknown codes generically).
 */
export const PlanChangeSchema = z.object({
    type: PlanChangeTypeSchema,
    mealId: z.string(),
    before: PlanChangeMealSnapshotSchema.nullable(),
    after: PlanChangeMealSnapshotSchema.nullable(),
    reasonCodes: z.array(z.string()),
    /** Present only on an offer-backed change (reasonCodes contains OFFER_MATCH) — the ADR-028 offer snapshot. */
    offers: z.array(PlanChangeOfferSchema).optional(),
});
/**
 * Weekly-budget verdict (mantra roadmap Step 2, 2026-08-31) — computed by
 * services/pantry-shopping's assessWeeklyBudget from pantry-netted,
 * pack-rounded shopping ROWS (what the user must BUY), and shared verbatim
 * by plan generation, mutation previews, the Shop screen, Fit Score, and
 * Amy. Honesty: 'unverifiable' whenever a required item lacks a price and
 * the known subtotal is under target — never 'within_budget' on partial
 * data; 'over_budget' is provable even with gaps once the known subtotal
 * exceeds the target.
 */
/**
 * ONE authoritative meal-serving model (owner decision 2026-09-04): the
 * portion multipliers the generator may choose (services/planning
 * SERVING_OPTIONS imports THIS), the only values a scale-servings proposal
 * may request, and the only stops the portion stepper may move through.
 * Recipe servings are NEVER household size.
 */
export const SUPPORTED_MEAL_SERVINGS = [1, 1.5, 2];
export const MealServingsSchema = z
    .number()
    .positive()
    .refine((v) => SUPPORTED_MEAL_SERVINGS.includes(v), {
    message: "servings must be one of 1, 1.5, 2",
});
export function nextSupportedServings(current, direction) {
    const options = SUPPORTED_MEAL_SERVINGS;
    const idx = options.indexOf(current);
    const next = idx === -1 ? (direction === 1 ? options.find((o) => o > current) : [...options].reverse().find((o) => o < current)) : options[idx + direction];
    return next ?? null;
}
export const BudgetVerdictSchema = z.object({
    status: z.enum(["no_target", "within_budget", "over_budget", "unverifiable"]),
    weeklyCostCents: z.number().int().nonnegative(),
    budgetCents: z.number().int().nonnegative().nullable(),
    gapCents: z.number().int().nullable(),
    unpricedItemCount: z.number().int().nonnegative(),
    /** Generation-time only: cheapest VALID plan cost found across the cost-pressure ladder (equals weeklyCostCents when the kept plan is that one). */
    cheapestValidCostCents: z.number().int().nonnegative().optional(),
    /** Generation-time only: cost-pressure ladder attempts actually run. */
    costPressureAttempts: z.number().int().positive().optional(),
    /** 2026-09-03 Budget Affordability: the SAME pack-rounded/pantry-netted
     * priced rows split into distinct economic facts — never conflated:
     * checkoutCash = real first-shop cash; consumedThisWeekValue = value of
     * PURCHASED food this week's meals use; pantryCarryoverValue = purchased
     * food remaining after the week (still paid for — checkout = consumed +
     * carryover by construction); estimatedWeeklyFoodValue = value of ALL
     * food the week's meals consume incl. pantry-covered — the steady-state
     * indicator ("a typical week of this plan eats about this much food
     * value"), an indicator, not a price promise. Computed over PRICED rows
     * only; pricedRowCount/unpricedRowCount state the coverage honestly. */
    economics: z
        .object({
        checkoutCashCents: z.number().int().nonnegative(),
        consumedThisWeekValueCents: z.number().int().nonnegative(),
        pantryCarryoverValueCents: z.number().int().nonnegative(),
        estimatedWeeklyFoodValueCents: z.number().int().nonnegative(),
        pricedRowCount: z.number().int().nonnegative(),
        unpricedRowCount: z.number().int().nonnegative(),
        /** Owner invariant 2026-09-04: true only when EVERY required row is
         * priced — the split below is then a complete picture. False = the
         * numbers cover priced rows only (partial) and must be hidden or
         * labelled "priced items only"; the forecast stays unavailable. */
        complete: z.boolean().optional(),
    })
        .optional(),
    /**
     * Owner invariant 2026-09-04 — CEILING-INDEPENDENT completeness of the
     * priced basket. `checkout.status` can only say "unverifiable" relative
     * to a maximum; with no maximum it says `no_max` even when required rows
     * are unpriced. This block states completeness on its own: `complete`
     * = every required (non-manual buy>0, plus manual) row has a reliable
     * price. When false, `pricedSubtotalCents` is a PARTIAL subtotal and
     * must never be headlined as the checkout estimate ("Priced so far €X ·
     * N items still need a reliable price").
     */
    pricing: z
        .object({
        complete: z.boolean(),
        pricedRowCount: z.number().int().nonnegative(),
        unpricedRequiredCount: z.number().int().nonnegative(),
        pricedSubtotalCents: z.number().int().nonnegative(),
    })
        .optional(),
    /**
     * Budget Model v2 (ADR-052, 2026-09-04): the CHECKOUT verdict — this
     * week's first-shop checkout cash against the HARD ceiling (the user's
     * optional maximum checkout, or — `maxSource: "legacy_limit"` — a pre-v2
     * weekly budget the user has not yet classified). `exceeds_max` fails a
     * rebuild / blocks an apply with NO override. Optional only for revisions
     * persisted before v2.
     */
    checkout: z
        .object({
        costCents: z.number().int().nonnegative(),
        maxCents: z.number().int().nonnegative().nullable(),
        maxSource: z.enum(["maximum_checkout", "legacy_limit"]).nullable(),
        status: z.enum(["no_max", "within_max", "exceeds_max", "unverifiable"]),
        /** costCents − maxCents (negative = headroom); null when no ceiling. */
        deltaCents: z.number().int().nullable(),
    })
        .optional(),
    /**
     * Budget Model v2: the GOAL verdict — the FORECAST weekly average (the
     * ADR-050 steady-state estimate of THIS plan; an estimate, never actual
     * spending) against the SOFT weekly spending goal. EXACT integer
     * comparison and a signed exact delta — there is no tolerance band
     * (owner decision D-3). `unavailable` when there is no goal, the account
     * still carries an unclassified legacy limit (`legacy_unclassified`), or
     * any required row is unpriced (a partial forecast is a lower bound and
     * is never served as the forecast).
     */
    goal: z
        .object({
        forecastWeeklyAverageCents: z.number().int().nonnegative().nullable(),
        goalCents: z.number().int().nonnegative().nullable(),
        status: z.enum(["at_or_below_goal", "above_goal", "unavailable"]),
        /** forecast − goal (negative = below goal); null unless both exist. */
        deltaCents: z.number().int().nullable(),
        unavailableReason: z.enum(["no_goal", "legacy_unclassified", "unpriced_rows", "no_economics"]).nullable(),
    })
        .optional(),
});
export const PlanImpactSchema = z.object({
    shoppingDelta: z.number().describe("EUR delta on the shopping list total."),
    estimatedTimeDeltaMinutes: z.number(),
    pantryReservationsChanged: z.boolean(),
    /** Step 2 mutation guard: the RESULTING week's budget verdict if this proposal is applied — an over_budget swap must never read as a plain success. */
    budget: BudgetVerdictSchema.optional(),
    /** Budget Model v2 (ADR-052): true when applying would take this week's
     * CHECKOUT over the HARD ceiling (maximum checkout, or an unclassified
     * legacy limit). Apply then fails 422 MAX_CHECKOUT_CONFLICT — there is NO
     * override flag; the user changes or removes the maximum in Settings. */
    checkoutConflict: z.boolean().optional(),
    /** @deprecated pre-v2 name of `checkoutConflict` (mirrored for one
     * release so unmigrated clients keep rendering the conflict). */
    budgetConflict: z.boolean().optional(),
    /** Budget Model v2: the SOFT goal verdict before/after this change — pure
     * information (exact forecast delta), never a block, never a consent. */
    goalImpact: z
        .object({
        beforeForecastWeeklyAverageCents: z.number().int().nonnegative().nullable(),
        afterForecastWeeklyAverageCents: z.number().int().nonnegative().nullable(),
        goalCents: z.number().int().nonnegative().nullable(),
        afterStatus: z.enum(["at_or_below_goal", "above_goal", "unavailable"]),
    })
        .optional(),
    /** Step-6 correction (ADR-043): the RESULTING plan's ACTIONABLE
     * (remaining-week, today onward) grocery-cohesion metrics — compare
     * against GET /v1/plans/current planMetrics.remainingWeek, never
     * fullWeek. Loosely typed to avoid a schema cycle; shape =
     * PlanWasteMetricsSchema. */
    wasteMetrics: z.record(z.string(), z.number()).optional(),
});
export const MakeRoomResultSchema = z.object({
    objective: z.enum(["day_range", "target"]),
    boundKcal: z.number().int(),
    anchorMealId: z.string(),
    anchorServings: z.number().positive(),
    anchorDayKcal: z.number().int(),
    neededKcal: z.number().int().nonnegative(),
    adjustment: z.enum(["none", "portion", "replacement"]),
    adjustedMealId: z.string().nullable(),
    removedKcal: z.number().int().nonnegative(),
});
/** ADR-058 — one supported portion of the same recipe, judged against the live day's window and floor. */
export const FitMealPortionCheckSchema = z.object({
    servings: z.number().positive(),
    mealKcal: z.number().int().nonnegative(),
    dayKcal: z.number().int().nonnegative(),
    fits: z.boolean(),
    reason: z.enum(["fits", "current", "above_bound", "below_floor", "below_range"]),
});
/**
 * ADR-058 — present on a `fit_meal` proposal: the authoritative figures the
 * fit was decided on (live day without the meal, bound, range bottom,
 * safety floor, the allowance they imply), every supported portion of the
 * same recipe with its verdict, what was chosen (largest fitting portion →
 * fitting swap → none when the meal already fits) and how many eligible swap
 * candidates were evaluated. The same figures ride the 422 NO_FIT_FOUND
 * details when nothing fits. Clients and Amy explain from these only.
 */
export const FitMealResultSchema = z.object({
    objective: z.enum(["day_range", "target"]),
    mealId: z.string(),
    currentServings: z.number().positive(),
    currentMealKcal: z.number().int().nonnegative(),
    dayKcalWithoutMeal: z.number().int().nonnegative(),
    targetKcal: z.number().int().nonnegative(),
    boundKcal: z.number().int().nonnegative(),
    rangeBottomKcal: z.number().int().nonnegative(),
    floorKcal: z.number().int().nonnegative(),
    allowance: z.object({ maxKcalForBound: z.number().int().nonnegative(), minKcalForFloor: z.number().int().nonnegative() }),
    portions: z.array(FitMealPortionCheckSchema),
    chosen: z.enum(["none", "portion", "swap"]),
    chosenRecipeId: z.string(),
    chosenServings: z.number().positive(),
    chosenMealKcal: z.number().int().nonnegative(),
    chosenDayKcal: z.number().int().nonnegative(),
    swapCandidatesEvaluated: z.number().int().nonnegative(),
    swapCandidatesFitting: z.number().int().nonnegative(),
});
export const PlanProposalResponseSchema = z
    .object({
    proposalId: z.string(),
    planId: z.string(),
    baseRevision: z.number().int().nonnegative(),
    scope: z.object({ mealIds: z.array(z.string()) }),
    changes: z.array(PlanChangeSchema),
    validation: ValidationResultSchema,
    impact: PlanImpactSchema,
    /**
     * Only ever issued for a PASSed validation (B1 fix) — a proposal whose
     * validation FAILed must never be applyable, so the server never mints a
     * confirmationToken for it and this field is null instead. See the
     * superRefine below, which makes that invariant part of the contract
     * itself rather than a convention callers can drift from.
     */
    confirmationToken: z.string().nullable(),
    expiresAt: IsoDateTimeSchema,
    /**
     * ADR-055 acceptance 3 — present on a `make_room` proposal: which
     * objective the owner optimized, the bound it used, the anchored day's
     * kcal with the anchor applied, how much had to be removed elsewhere,
     * and the ONE other meal it adjusted (or `none` when the anchor already
     * fit). Clients and Amy explain from these figures only.
     */
    makeRoom: MakeRoomResultSchema.optional(),
    /** ADR-058 — present on a `fit_meal` proposal (see FitMealResultSchema). */
    fit: FitMealResultSchema.optional(),
})
    .superRefine((val, ctx) => {
    // ADR-055 precision (2026-09-04): an EMPTY diff is never approvable — the
    // server never mints a token for it (the 2026-08-26 empty-diff guard; a
    // scale to the meal's current serving is the everyday case) — so the
    // invariant is "PASS with changes ⇒ token" and "no changes ⇒ no token".
    if (val.validation.status === "PASS" && val.changes.length > 0 && val.confirmationToken === null) {
        ctx.addIssue({
            code: "custom",
            path: ["confirmationToken"],
            message: "confirmationToken must be non-null when validation.status is PASS and the proposal carries changes",
        });
    }
    if (val.changes.length === 0 && val.confirmationToken !== null) {
        ctx.addIssue({
            code: "custom",
            path: ["confirmationToken"],
            message: "confirmationToken must be null when the proposal carries no changes (an empty diff is never approvable)",
        });
    }
    if (val.validation.status === "FAIL" && val.confirmationToken !== null) {
        ctx.addIssue({
            code: "custom",
            path: ["confirmationToken"],
            message: "confirmationToken must be null when validation.status is FAIL",
        });
    }
});
// ---------------------------------------------------------------------------
// Error envelopes
// ---------------------------------------------------------------------------
/**
 * Flat HTTP API error envelope (T04 instructions: code, messageKey,
 * correlationId, retryable).
 */
export const ErrorEnvelopeSchema = z.object({
    code: z.string(),
    messageKey: z.string(),
    correlationId: z.string(),
    retryable: z.boolean(),
    details: z.record(z.string(), z.unknown()).optional(),
});
/** 409 on stale aggregate writes (api-contracts.md contract rules). */
export const StaleRevisionErrorSchema = ErrorEnvelopeSchema.extend({
    code: z.literal("STALE_REVISION"),
    retryable: z.literal(false),
    details: z.object({
        expectedRevision: z.number().int(),
        receivedRevision: z.number().int(),
    }),
});
/**
 * Tool-call error envelope used by Amy's Gateway tools (api-contracts.md
 * "Tool error envelope") — nested under `error`, distinct wire shape from
 * the flat HTTP ErrorEnvelope above.
 */
export const ToolErrorEnvelopeSchema = z.object({
    error: z.object({
        code: z.string(),
        messageKey: z.string(),
        retryable: z.boolean(),
        correlationId: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
    }),
});
// ---------------------------------------------------------------------------
// Entitlement summary (shared by /v1/me and billing_entitlement tool)
// ---------------------------------------------------------------------------
export const EntitlementSourceSchema = z.enum(["pilot", "individual", "b2b", "none"]);
export const EntitlementStateSchema = z.enum([
    "active",
    "trialing",
    "past_due",
    "canceled",
    "expired",
    "none",
]);
export const EntitlementSummarySchema = z.object({
    source: EntitlementSourceSchema,
    state: EntitlementStateSchema,
    trialEndsAt: IsoDateTimeSchema.nullable(),
    currentPeriodEnd: IsoDateTimeSchema.nullable(),
    canBeCharged: z.boolean().describe("Always false for pilot-sourced entitlements."),
});
// ---------------------------------------------------------------------------
// "none" sentinel used by mandatory-explicit-answer onboarding fields
// ---------------------------------------------------------------------------
export const NoneSentinelSchema = z.literal("none");
export const explicitListOrNone = (item) => z.union([NoneSentinelSchema, z.array(item)]);
//# sourceMappingURL=primitives.js.map